import type { Implementation, MessageRouter } from "@pdfking/functionpool"
import { CachedForward, CacheMode } from "@pdfking/functionpool"
import type {
  Executor,
  Struct,
  TaskContext,
  TaskDescriptor,
  TaskImplementations,
  TaskMap,
  TrackingCallback,
  TrackingStatus,
} from "@pdfking/planner"
import { CreateExecutor, TaskExec, TaskIO, TrackingEnum } from "@pdfking/planner"
import { Tasks as PlannerTasks } from "@pdfking/planner/tasks"
import type { StringGUID } from "@pdfking/tfunc"
import {
  Channel,
  Collect,
  CreateSingleton,
  Join,
  MapAsync,
  Only,
  ProcessIterable,
} from "@pdfking/tfunc"

import type { EngineOptions, ExecutionContext } from "#public"
import type { TaskOptions, WireInput, WireOutput, WireRequest, WireResponse } from "#public"

import { WireEnum } from "../public/types/WireTypes"

// the wire carries one input at most: a VECTOR task is a planner operation,
// those run inline (see ExecutorFromRouter)
const marshal = (value: unknown, io: TaskIO) =>
  (io === TaskIO.ATOM ? [value] : value) as AsyncIterable<unknown>

const unmarshal = (stream: AsyncIterable<unknown>, io: TaskIO) =>
  io === TaskIO.ATOM ? Only(stream as AsyncGenerator<unknown>) : stream

const ExecutorFromRouter = <TM extends TaskMap>(
  forward: MessageRouter<WireRequest, WireResponse>,
  descriptor: TaskDescriptor,
  tracking?: TrackingCallback
): Executor<TM, ExecutionContext> => {
  const call = async function* (
    name: string,
    guid: StringGUID,
    data: Struct,
    input: unknown,
    cache_evict: boolean | undefined
  ) {
    const request = MapAsync(
      value => ({ enum: WireEnum.INPUT, value }) as WireRequest,
      marshal(input, descriptor[name].input)
    )

    for await (const response of forward(
      Join(
        [{ enum: WireEnum.TASK, cache_evict, guid, value: { name, data } } as WireRequest],
        request
      )
    )) {
      if (response.enum === WireEnum.TRACKING) {
        tracking?.(response.value)
      } else {
        yield response.value
      }
    }
  }

  // an INLINE task is a planner operation (Preload, Take, ...): it runs in the
  // thread that called it, NORMAL and CACHED ones cross the wire
  const proxy = new Proxy({} as TaskImplementations<TM, ExecutionContext>, {
    get: (_, name: string) => {
      if (descriptor[name].exec === TaskExec.INLINE)
        return PlannerTasks[name as keyof typeof PlannerTasks]

      const handler = (
        { context, guid }: TaskContext<TM, ExecutionContext>,
        data: Struct,
        input: unknown
      ) => call(name, guid, data, input, context?.cache_evict)

      return descriptor[name].output === TaskIO.ATOM
        ? (...args: Parameters<typeof handler>) => Only(handler(...args))
        : handler
    },
  })

  return CreateExecutor<TM, ExecutionContext>(proxy, descriptor, tracking)
}

export const EngineMaker = <TM extends TaskMap>(
  factory: (
    forward: MessageRouter<WireRequest, WireResponse>
  ) => MessageRouter<WireRequest, WireResponse>,
  descriptor: TaskDescriptor,
  options?: EngineOptions
): Executor<TM, ExecutionContext> => {
  const router = CreateSingleton(() => factory(forward))

  const forward: MessageRouter<WireRequest, WireResponse> = CachedForward(
    t => router()(t),
    async input => {
      const [opts] = (await Collect(input, 1)) as [TaskOptions]
      return {
        guid: opts.guid,
        input: Join([opts], input),
        mode:
          descriptor[opts.value.name].exec !== TaskExec.CACHED
            ? CacheMode.SKIP
            : opts.cache_evict
              ? CacheMode.EVICT
              : CacheMode.USE,
      }
    },
    response => response.enum === WireEnum.OUTPUT,
    options?.cache
  )

  return ExecutorFromRouter<TM>(forward, descriptor, options?.tracking)
}

export const ReceiverMaker = <TM extends TaskMap>(
  tasks: TaskImplementations<TM, ExecutionContext>,
  descriptor: TaskDescriptor
): Implementation<WireRequest, WireResponse> =>
  async function* (input, forward) {
    const [
      {
        cache_evict,
        value: { name, data },
        guid,
      },
    ] = (await Collect(input, 1)) as [TaskOptions]

    const io = descriptor[name]
    const raw = MapAsync(msg => msg.value, input as AsyncGenerator<WireInput>)

    const { push, stop, stream } = Channel<WireResponse>()

    const emit = (status: TrackingStatus) =>
      push({
        enum: WireEnum.TRACKING,
        value: { enum: TrackingEnum.STATUS, guid, ...status },
      })

    const engine = ExecutorFromRouter<TM>(forward, descriptor, msg =>
      push({ enum: WireEnum.TRACKING, value: msg })
    )

    const result = (tasks[name as keyof TM & string] as any)(
      { context: { cache_evict }, emit, guid, ...engine },
      data,
      await unmarshal(raw, io.input)
    )

    void ProcessIterable(
      MapAsync(
        value => ({ enum: WireEnum.OUTPUT, value }) as WireOutput,
        marshal(result, io.output)
      ),
      push,
      stop
    )

    yield* stream
  }
